import { Calendar, User, ArrowRight, Clock, ChevronLeft, ChevronRight, Code2, Cpu, Shield, Sparkles, Brain, Layers, Code, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { useState } from "react";
import Footer from "@/components/Footer";
import BlogPostImage from "@/components/BlogPostImage";

const Blog = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;

  const allPosts = [
    {
      id: "tendencias-desenvolvimento-web-2026",
      title: "Tendências em Desenvolvimento Web para 2026",
      excerpt: "Descubra as principais tecnologias e frameworks que estão moldando o futuro do desenvolvimento web. Exploramos IA integrada, WebAssembly, Edge Computing e muito mais.",
      date: "15 de Novembro, 2025",
      author: "Equipe Coddar",
      readTime: "8 min",
      image: "/assets/blog-tendencias-web-2026.webp",
      icon: <Code2 className="w-full h-full" />
    },
    {
      id: "automacao-transformar-negocio",
      title: "Como a Automação Pode Transformar Seu Negócio",
      excerpt: "Entenda como processos automatizados podem aumentar a eficiência e reduzir custos operacionais. Veja casos de sucesso e aprenda como começar.",
      date: "12 de Novembro, 2025",
      author: "Equipe Coddar",
      readTime: "10 min",
      image: "/assets/blog-automacao-negocio.webp",
      icon: <Cpu className="w-full h-full" />
    },
    {
      id: "seguranca-digital-proteja-dados",
      title: "Segurança Digital: Proteja Seus Dados",
      excerpt: "Conheça as melhores práticas para manter seus dados e sistemas seguros contra ameaças digitais. Descubra as soluções Acronis e como implementar segurança efetiva.",
      date: "10 de Novembro, 2025",
      author: "Equipe Coddar",
      readTime: "12 min",
      image: "/assets/blog-seguranca-digital.webp",
      icon: <Shield className="w-full h-full" />
    },
    {
      id: "ia-generativa-revoluciona-desenvolvimento",
      title: "IA Generativa Revoluciona o Desenvolvimento de Software",
      excerpt: "Como ferramentas como ChatGPT, GitHub Copilot e Claude estão transformando a forma como desenvolvemos software. Descubra as melhores práticas e o futuro da programação assistida por IA.",
      date: "8 de Novembro, 2025",
      author: "Equipe Coddar",
      readTime: "9 min",
      image: "/assets/blog-IA-Generativa-Revoluciona-Desenvolvimento-Software.webp",
      icon: <Sparkles className="w-full h-full" />
    },
    {
      id: "chatgpt-o1-raciocinio-avancado",
      title: "ChatGPT o1: A Nova Era do Raciocínio em IA",
      excerpt: "OpenAI lança modelo o1 com capacidades avançadas de raciocínio. Entenda como essa evolução impacta resolução de problemas complexos, matemática e programação.",
      date: "5 de Novembro, 2025",
      author: "Equipe Coddar",
      readTime: "7 min",
      image: "/assets/blog-ChatGPT-o1-Nova-Era.webp",
      icon: <Brain className="w-full h-full" />
    },
    {
      id: "gemini-2-google-multimodal",
      title: "Gemini 2.0: Google Avança na IA Multimodal",
      excerpt: "A nova versão do Gemini traz integração nativa com imagens, vídeos e áudio. Veja como essa tecnologia pode ser aplicada em seus projetos e negócios.",
      date: "2 de Novembro, 2025",
      author: "Equipe Coddar",
      readTime: "8 min",
      image: "/assets/blog-Gemini-2.0-Google-IA-Multimodal.webp",
      icon: <Layers className="w-full h-full" />
    },
    {
      id: "ia-codigo-aberto-llama-mistral",
      title: "IA de Código Aberto: Llama 3 e Mistral Desafiam Gigantes",
      excerpt: "Modelos open-source como Llama 3.1 e Mistral Large 2 competem com GPT-4. Descubra as vantagens de usar IA de código aberto em sua empresa.",
      date: "30 de Outubro, 2025",
      author: "Equipe Coddar",
      readTime: "10 min",
      image: "/assets/blog-Llama-3-Mistral-Desafiam-Gigantes.webp",
      icon: <Code className="w-full h-full" />
    },
    {
      id: "agentes-ia-autonomos-futuro",
      title: "Agentes de IA Autônomos: O Futuro da Automação",
      excerpt: "De assistentes virtuais a sistemas que tomam decisões complexas. Explore como agentes de IA estão mudando a automação empresarial e o que esperar nos próximos anos.",
      date: "27 de Outubro, 2025",
      author: "Equipe Coddar",
      readTime: "11 min",
      image: "/assets/blog-Agentes-IA-Autônomos-Futuro-da-Automacao.webp",
      icon: <Bot className="w-full h-full" />
    }
  ];

  const totalPages = Math.ceil(allPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = allPosts.slice(indexOfFirstPost, indexOfLastPost);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">
              Início
            </Link>
            <Link to="/blog" className="text-primary font-medium">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4">
            Blog <span className="text-primary">Coddar</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Insights, dicas e novidades sobre tecnologia, desenvolvimento e inovação
          </p>
        </div>
      </section>

      {/* Posts List */}
      <section className="py-12 px-4 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="space-y-6">
            {currentPosts.map((post, index) => (
              <Link key={index} to={`/blog/${post.id}`} className="block group">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-lg overflow-hidden">
                  <div className="flex flex-col md:flex-row gap-6 p-6">
                    {/* Image */}
                    <div className="md:w-64 flex-shrink-0">
                      <div className="aspect-video md:h-32 overflow-hidden rounded-lg">
                        <BlogPostImage
                          title={post.title}
                          backgroundImage={post.image}
                          icon={post.icon}
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {post.readTime}
                          </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-2xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                          {post.title}
                        </h2>

                        {/* Excerpt */}
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                      </div>

                      {/* Read More Link */}
                      <div className="inline-flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                        Ler artigo completo <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="border-primary/30 hover:border-primary/50 hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                  <Button
                    key={number}
                    variant={currentPage === number ? "default" : "outline"}
                    size="icon"
                    onClick={() => paginate(number)}
                    className={
                      currentPage === number
                        ? "bg-primary text-primary-foreground"
                        : "border-primary/30 hover:border-primary/50 hover:bg-primary/10"
                    }
                  >
                    {number}
                  </Button>
                ))}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="border-primary/30 hover:border-primary/50 hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Posts Count */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Mostrando {indexOfFirstPost + 1} - {Math.min(indexOfLastPost, allPosts.length)} de {allPosts.length} artigos
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
